///
/// EarthBrowser
///
/// Created by Matt Giger on 10/28/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The control row under the strip: playback split button (play/pause +
/// speed), the fused LIVE/clock chip and the expand chevron. Pure view
/// over the model. Frame stepping has no buttons here (the scrubber
/// covers it); the , and . keys remain.

import { TimeValueFieldCompact } from "@/components/header/time/TimeValueFieldCompact"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { formatRelativeOffset } from "@/utils/timeControl"
import { cn } from "@/utils/utils"
import { ChevronDownIcon, ChevronUpIcon, LockIcon, PauseIcon, PlayIcon } from "lucide-react"
import { useCallback, useState } from "react"
import { cSpeedPresets, formatSpeed, type TimeControlModel } from "./timeControlModel"

export function TransportRow({ model }: { model: TimeControlModel }) {
	const { actions, isAnimating, isLive, speed } = model

	// Speed popover is controlled so picking a preset dismisses it
	const [speedMenuOpen, setSpeedMenuOpen] = useState(false)

	// Phones: the open date editor is wider than the row allows, so the
	// whole clock chip takes its own full-width line under the controls
	const [clockEditing, setClockEditing] = useState(false)
	const onClockEditing = useCallback((editing: boolean) => setClockEditing(editing), [])

	// Offset from live, shown under the LIVE label while not live: how far
	// the view is from now, coloured like the strip's past (blue) / future
	// (amber) zones. The LIVE label itself stays (dim, hollow dot) so the
	// slot still reads as the way back to live.
	const offsetLabel = formatRelativeOffset(model.offsetFromNowMs)
	const atNow = offsetLabel === "now"
	const behind = model.offsetFromNowMs < 0
	const offsetClass = atNow ? "text-white/55" : behind ? "text-blue-300" : "text-amber-300"

	return (
		<div className="flex flex-row flex-wrap sm:flex-nowrap items-center gap-1 px-1.5 sm:gap-1.5 sm:px-2 py-1">
			{/* Playback split button: play/pause on the left, the speed on the
			    right opens the preset popover. One control, since speed only
			    matters while animating. Neutral styling: green stays reserved
			    for LIVE/now. */}
			<div className={cn(
				"flex items-stretch shrink-0 rounded-lg overflow-hidden transition-colors duration-200",
				isAnimating ? "bg-orange-500/20" : "bg-white/10"
			)}>
				<button
					onClick={actions.togglePlay}
					className="h-9 px-2.5 hover:bg-white/15 transition-colors flex items-center"
					title={isAnimating ? "Pause (Space)" : isLive ? "Play from the start of the time window (Space)" : "Play from the displayed time (Space)"}
					aria-label={isAnimating ? "Pause" : "Play"}
				>
					{isAnimating
						? <PauseIcon className="w-4 h-4 stroke-orange-200 stroke-[2.5]" />
						: <PlayIcon className="w-4 h-4 stroke-white/90 stroke-[2.5]" />}
				</button>
				<Popover open={speedMenuOpen} onOpenChange={setSpeedMenuOpen}>
					<PopoverTrigger asChild>
						<button
							className={cn(
								"h-9 flex items-center gap-0.5 pl-1.5 pr-1 border-l border-white/10 text-[10px] font-mono font-bold tabular-nums transition-colors hover:bg-white/15",
								speed.capped ? "text-purple-300" : isAnimating ? "text-orange-200" : "text-white/70"
							)}
							title={speed.capped
								? `Speed limited to ${formatSpeed(speed.cap ?? 0)} while tracking a satellite (set to ${formatSpeed(speed.configured)})`
								: `Animation speed: ${formatSpeed(speed.effective)} — click to change`}
							aria-label="Choose animation speed"
						>
							{speed.capped && <LockIcon className="w-2.5 h-2.5" />}
							{formatSpeed(speed.effective)}
							<ChevronUpIcon className="w-3 h-3 opacity-70" />
						</button>
					</PopoverTrigger>
					<PopoverContent
						side="top"
						align="start"
						sideOffset={8}
						className="w-auto p-1 rounded-xl bg-black/80 backdrop-blur-xl border-white/20 text-white shadow-2xl"
					>
						<div className="flex flex-col gap-0.5" role="menu" aria-label="Animation speed">
							{cSpeedPresets.map(preset => {
								const isLimited = speed.cap !== null && preset.value > speed.cap
								const isCurrent = speed.configured === preset.value
								return (
									<button
										key={preset.value}
										role="menuitemradio"
										aria-checked={isCurrent}
										disabled={isLimited}
										onClick={() => {
											actions.setSpeed(preset.value)
											setSpeedMenuOpen(false)
										}}
										className={cn(
											"flex items-center justify-between gap-3 px-2 py-1.5 rounded-lg text-left transition-colors",
											isLimited
												? "text-white/25 cursor-not-allowed"
												: isCurrent
												? "bg-orange-500/25 text-orange-200"
												: "text-white/80 hover:bg-white/10"
										)}
										title={isLimited ? `Limited to ${formatSpeed(speed.cap ?? 0)} while tracking a satellite` : undefined}
									>
										<span className="font-mono text-xs font-bold w-10">{preset.label}</span>
										<span className="text-[10px] text-white/55">{preset.description}</span>
									</button>
								)
							})}
						</div>
					</PopoverContent>
				</Popover>
			</div>

			{/* LIVE + clock as one chip: the left segment is the real-time
			    toggle (a dim "go live" chip with the distance from now under
			    it while not live); the right segment is the editable clock
			    with its zone. Grows to absorb the row's slack. The live
			    segment steps aside while the editor is open (it has its own
			    Now button) so the bar's width barely moves */}
			<div
				className={cn(
					"flex-1 min-w-0 h-9 flex items-stretch rounded-lg overflow-hidden transition-colors duration-200",
					isLive ? "bg-green-500/15 ring-1 ring-green-500/40" : "bg-white/10",
					clockEditing && "max-sm:basis-full max-sm:order-last ring-1 ring-blue-500/50"
				)}
				data-timeline-clock
			>
				{!clockEditing && (
					<>
						<button
							onClick={actions.toggleLive}
							// Fixed width: the offset text changes length as it
							// ticks and must not shift the clock beside it
							className={cn(
								"flex flex-col items-center justify-center shrink-0 w-[4.5rem] leading-none transition-colors",
								isLive ? "hover:bg-green-500/15" : "hover:bg-white/10"
							)}
							title={isLive
								? "Tracking current time — click to pause"
								: atNow
								? "Paused at the current time — click to go live"
								: `${offsetLabel} ${behind ? "behind" : "ahead of"} the current time — click to go live`}
							aria-label="Toggle real time mode"
							aria-pressed={isLive}
						>
							<span className={cn(
								"flex items-center gap-1 font-bold tracking-wider",
								isLive ? "text-[10px] text-green-300" : "text-[9px] text-white/55"
							)}>
								<span
									className={cn(
										"w-1.5 h-1.5 rounded-full shrink-0",
										isLive ? "bg-green-400" : "border border-white/50"
									)}
									style={{ animation: isLive ? "timelinePulse 2s ease-in-out infinite" : "none" }}
								/>
								LIVE
							</span>
							{!isLive && (
								<span className={cn("mt-0.5 text-[10px] font-mono font-bold tabular-nums", offsetClass)}>
									{atNow ? "paused" : offsetLabel}
								</span>
							)}
						</button>
						<div className="w-px bg-white/10 my-1.5" />
					</>
				)}
				<TimeValueFieldCompact segment className="flex-1 min-w-0 justify-center" onEditingChange={onClockEditing} />
			</div>

			{/* No buffering chip here: it appeared and vanished with the data
			    and reflowed the whole bar. The strip's lane shimmer and the
			    scrub bubble carry the loading state in place */}
			<button
				onClick={actions.toggleExpanded}
				className="h-9 w-7 flex items-center justify-center shrink-0 rounded-lg hover:bg-white/10 transition-colors"
				title={model.isExpanded ? "Collapse timeline" : "Expand timeline: time window and data lanes"}
				aria-label="Toggle expanded view"
				aria-expanded={model.isExpanded}
			>
				{model.isExpanded
					? <ChevronDownIcon className="w-4 h-4 stroke-white/70" />
					: <ChevronUpIcon className="w-4 h-4 stroke-white/70" />}
			</button>
		</div>
	)
}
